/**
 * Team Component
 */

import React, { ReactElement } from 'react';
import { useTheme } from 'styled-components';
import { TeamProps } from './team.types';
import TeamLogo from '../TeamLogo/teamLogo';
import { TeamLogoTypes } from '../TeamLogo/teamLogo.types';
import {
  OuterWrapper,
  DataWrapper,
  NameWrapper,
  DesktopRecordWrapper,
  MobileRecordWrapper,
  DesktopStandingsWrapper,
  MobileStandingsWrapper,
  SplitSquadWrapper,
  FavoriteIconWrapper,
  FavIconContainer,
} from './team.style';
import wrapLink from '../../utils/linkWrapperHelper';
import Skeleton from '../Skeleton/skeleton';
import { useMedia } from '../../utils/hooks/useMedia/useMedia';
import BREAKPOINTS, { BreakpointKeys } from '../../style/breakpoints';

const Team = ({
  name,
  leftAligned = true,
  displayRecord = true,
  recordBelow = true,
  teamId,
  linkProps,
  wins = 0,
  losses = 0,
  ties,
  nameFontSize = 16,
  recordFontSize = 12,
  logoSize = 24,
  isLoading,
  splitSquad = '',
  skeletonNameWidth = 30,
  skeletonRecordWidth = 35,
  isMobile,
  breakpoint = BreakpointKeys.SM,
  teamLogoType = TeamLogoTypes.CAP,
  isFavorite,
  standingsText,
  alt,
  ariaHidden,
  fullTeamName,
  ariaLabel,
}: TeamProps) => {
  const { breakpoints = BREAKPOINTS, dark = false } = useTheme();
  const mobileWidth = typeof isMobile === 'undefined' ? useMedia({ maxWidth: breakpoints[breakpoint] }) : isMobile;
  const isSpot = teamLogoType === TeamLogoTypes.SPOT;
  // Calculate the side of our 1:1 ratio
  const logoSide: string | number = !isSpot ? `${logoSize}px` : logoSize;
  let favIconWidth;
  let favIconMargin;

  if (isSpot) {
    favIconWidth = Math.floor(logoSize * 0.42);
    favIconMargin = logoSize - favIconWidth;
  }

  // dynamically display loader skeleton or UI based on isLoading
  const nameContent = isLoading ? (
    <Skeleton width={skeletonNameWidth} height={displayRecord ? 12 : 16} margin={2} />
  ) : (
    name
  );

  const formattedRecord = `${wins} - ${losses} ${typeof ties !== 'undefined' ? `- ${ties}` : ``}`;

  const recordContent = isLoading ? <Skeleton width={skeletonRecordWidth} height={8} margin={0} /> : formattedRecord;

  const standingsContent = isLoading ? <Skeleton width={skeletonRecordWidth} height={8} margin={0} /> : standingsText;

  const splitSquadContent =
    isLoading || !splitSquad ? null : (
      <SplitSquadWrapper leftAligned={leftAligned} recordBelow={recordBelow}>
        {splitSquad}
      </SplitSquadWrapper>
    );

  const RecordWrapper = mobileWidth ? MobileRecordWrapper : DesktopRecordWrapper;
  const StandingsWrapper = mobileWidth ? MobileStandingsWrapper : DesktopStandingsWrapper;

  const FavoriteIcon = isFavorite && !isLoading && <FavoriteIconWrapper leftAligned={leftAligned} />;

  // Build the record to (potentially) have a link wrapped around

  const record: ReactElement = (
    <React.Fragment>
      <TeamLogo
        width={logoSide}
        height={logoSide}
        teamId={teamId}
        teamLogoType={teamLogoType}
        isLoading={isLoading}
        skeletonRounded
        alt={alt}
      />
      {isSpot && isFavorite ? (
        <FavIconContainer
          $recordBelow={recordBelow}
          $width={logoSize}
          $favIconWidth={favIconWidth}
          $favIconMargin={favIconMargin}
        >
          {FavoriteIcon}
        </FavIconContainer>
      ) : null}
      {splitSquadContent}
      <DataWrapper
        logoSize={logoSize}
        recordBelow={recordBelow}
        leftAligned={leftAligned}
        displayRecord={displayRecord}
        displayStanding={!!standingsContent}
        fontSize={nameFontSize}
      >
        <NameWrapper
          fontSize={nameFontSize}
          dark={!!dark}
          recordBelow={recordBelow}
          leftAligned={leftAligned}
          isFavorite={!isSpot && isFavorite}
          aria-label={fullTeamName}
        >
          {!isSpot && FavoriteIcon}
          {nameContent}
        </NameWrapper>
        {displayRecord && (
          <RecordWrapper
            recordBelow={recordBelow}
            fontSize={recordFontSize}
            leftAligned={leftAligned}
            aria-label={ariaLabel}
          >
            <span aria-hidden="true">{recordContent}</span>
          </RecordWrapper>
        )}
        {standingsText && (
          <StandingsWrapper recordBelow={recordBelow} fontSize={recordFontSize} leftAligned={leftAligned}>
            {standingsContent}
          </StandingsWrapper>
        )}
      </DataWrapper>
    </React.Fragment>
  );

  // Wrap a link if it exists
  const wrappedRecord: ReactElement = <div>{wrapLink(record, linkProps)}</div>;

  return (
    <OuterWrapper
      dark={!!dark}
      recordBelow={recordBelow}
      leftAligned={leftAligned}
      displayRecord={displayRecord}
      aria-hidden={ariaHidden}
    >
      {wrappedRecord}
    </OuterWrapper>
  );
};

export default Team;
